import { useMemo } from 'react'
import { Outlet, useParams } from 'react-router-dom-v5-compat'

import { useQuery } from '@apollo/client'

import { Button, MessageCircleHeartIcon } from '@buffer-mono/popcorn'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { PageLayout } from '~publish/components/PageLayout'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import { range } from '~publish/helpers/range'
import { isOfType } from '~publish/helpers/typeGuards'
import { useInMemoryRestoreScroll } from '~publish/hooks/scroll/useRestoreScroll'
import { usePageTitle } from '~publish/hooks/usePageTitle'

import { useFeedItems } from '../../hooks/useFeedItems'
import { useRefreshFeed } from '../../hooks/useRefreshFeed'
import {
  FeedArticleCardSkeleton,
  FeedArticleLoadFailure,
} from '../FeedArticleList/FeedArticleCard'
import { FeedArticleList } from '../FeedArticleList/FeedArticleList'
import { ListHeaderTitle } from '../ListHeader/ListHeaderTitle'

import styles from './FeedList.module.css'

const FeedListFeed = graphql(/* GraphQL */ `
  fragment FeedList_Feed on Feed {
    id
    name
    lastRefreshed
    connectionMetadata {
      ... on RSSFeedConnectionMetadata {
        __typename
        url
      }
      ... on NewsSearchConnectionMetadata {
        __typename
        query
      }
    }
  }
`)

const GetFeedListDetails = graphql(
  /* GraphQL */ `
    query GetFeedListDetails($input: FeedInput!) {
      feed(input: $input) {
        ...FeedList_Feed
      }
    }
  `,
  [FeedListFeed],
)

function dataIsLoaded<T>(feed: T | null | undefined): feed is T {
  return !!feed
}

export const FeedList = (): JSX.Element | null => {
  const { feedId } = useParams<{ feedId: string }>()
  const organizationId = useOrganizationId()

  const [refreshFeed] = useRefreshFeed()
  const pageLayoutScrollableRef = useInMemoryRestoreScroll(feedId ?? '')
  const { data, error } = useQuery(GetFeedListDetails, {
    variables: {
      input: {
        id: feedId ?? '',
        organizationId: organizationId ?? '',
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  })
  const feed = readFragment(FeedListFeed, data?.feed)

  const {
    loading: loadingFeedItems,
    items,
    fetchingMore,
    lastElementRef,
    error: feedItemsError,
  } = useFeedItems({
    organizationId,
    feedIds: [feedId ?? ''],
  })

  usePageTitle(feed?.name ? `${feed.name} (Feed)` : 'Feeds')
  const loadState = useMemo(() => {
    if (loadingFeedItems && !items?.length) {
      return { state: 'loading' } as const
    }
    if (items?.length) {
      return { state: 'success', items } as const
    }
    if (error || feedItemsError) {
      return { state: 'error', error: error || feedItemsError } as const
    }
    return { state: 'loading' } as const
  }, [error, feedItemsError, items, loadingFeedItems])

  const feedUrl = isOfType(
    feed?.connectionMetadata,
    'RSSFeedConnectionMetadata',
  )
    ? feed.connectionMetadata.url
    : undefined

  const lastUpdated = feed?.lastRefreshed ?? new Date().toISOString()

  return (
    <>
      <Outlet />
      <PageLayout className={styles.pageLayout} ref={pageLayoutScrollableRef}>
        <PageLayout.Header>
          <PageLayout.HeaderRow>
            <ListHeaderTitle
              title={feed?.name ?? ''}
              isCollection={false}
              domain={feedUrl}
              isLoading={!dataIsLoaded(feed)}
              lastUpdated={lastUpdated}
              onRefresh={async (): Promise<{
                success: boolean
                message: string
              }> => {
                if (!feed || !organizationId) {
                  return {
                    success: false,
                    message: 'Feed not found',
                  }
                }
                try {
                  const { error } = await refreshFeed({
                    variables: {
                      input: {
                        feedId: feed.id,
                        organizationId,
                      },
                    },
                  })
                  if (!error) {
                    return {
                      success: true,
                      message: 'Your feed has been refreshed!',
                    }
                  }
                  // eslint-disable-next-line unused-imports/no-unused-vars
                } catch (_error) {
                  // Do nothing, generic error shown below
                }
                return {
                  success: false,
                  message: 'Failed to refresh feed',
                }
              }}
            />
            <PageLayout.Actions>
              <FeedbackWidget id="content-feeds-1" source="create">
                <Button variant="tertiary" size="large">
                  <MessageCircleHeartIcon aria-hidden="true" />
                  Share Feedback
                </Button>
              </FeedbackWidget>
            </PageLayout.Actions>
          </PageLayout.HeaderRow>
        </PageLayout.Header>
        <PageLayout.Container size="narrow">
          {loadState.state === 'error' && <FeedArticleLoadFailure />}
          {loadState.state === 'loading' && (
            <div aria-live="polite" aria-label="Loading feed items">
              {range(20).map((index) => (
                <FeedArticleCardSkeleton key={index} />
              ))}
            </div>
          )}
          {loadState.state === 'success' && (
            <FeedArticleList
              items={loadState.items}
              fetchingMore={fetchingMore}
              lastElementRef={lastElementRef}
            />
          )}
        </PageLayout.Container>
      </PageLayout>
    </>
  )
}
